import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Users,
  AlertTriangle,
  Clock,
  FileCheck,
  RefreshCw,
  ChevronRight,
  UserCheck,
  UserX,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const divisionIcons = {
  Pool: Stethoscope,
  Mariner: Stethoscope,
  Skippers: Stethoscope,
  Navigator: Stethoscope,
  Camper: Stethoscope,
  Boating: Stethoscope,
  "Health Center": Stethoscope,
  Supervisors: Stethoscope,
  Other: Stethoscope,
};

export default function MedicalDashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expandedDivisions, setExpandedDivisions] = useState({});

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/medical/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch {
      toast.error("Failed to load medical dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/medical/campminder/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      // Poll sync status
      const poll = setInterval(async () => {
        try {
          const status = await axios.get(
            `${API}/medical/campminder/sync-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (status.data.status === "complete") {
            clearInterval(poll);
            setSyncing(false);
            fetchStats();
            toast.success(
              `Sync complete: ${status.data.last_sync_count} persons synced`
            );
          } else if (status.data.status === "error") {
            clearInterval(poll);
            setSyncing(false);
            toast.error(`Sync error: ${status.data.error}`);
          }
        } catch {
          clearInterval(poll);
          setSyncing(false);
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="medical-dashboard">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Medical Forms Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Physical Part 2 forms — Staff & Campers
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-teal-700 hover:bg-teal-800 btn-lift"
          data-testid="medical-sync-button"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`}
          />
          {syncing ? "Syncing..." : "Sync CampMinder"}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div
          className="bg-white border border-slate-200 rounded-lg p-5 card-animate stagger-1"
          data-testid="medical-stat-total"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-teal-700" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Total
              </p>
              <p className="text-2xl font-heading font-bold text-slate-900">
                {stats?.total_persons || 0}
              </p>
              <p className="text-xs text-slate-400">
                {stats?.total_staff || 0} staff · {stats?.total_campers || 0}{" "}
                campers
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-lg p-5 card-animate stagger-2"
          data-testid="medical-stat-compliant"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <FileCheck
                className="h-5 w-5 text-emerald-600"
                strokeWidth={2}
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Have Form
              </p>
              <p className="text-2xl font-heading font-bold text-emerald-600">
                {stats?.has_form || 0}
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-lg p-5 card-animate stagger-3"
          data-testid="medical-stat-missing"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <UserX className="h-5 w-5 text-amber-600" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Missing
              </p>
              <p className="text-2xl font-heading font-bold text-amber-600">
                {stats?.missing_form || 0}
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-lg p-5 card-animate stagger-4"
          data-testid="medical-stat-expired"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle
                className="h-5 w-5 text-red-600"
                strokeWidth={2}
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Expired
              </p>
              <p className="text-2xl font-heading font-bold text-red-600">
                {stats?.expired || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts by Division */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(stats?.alerts_by_division || {}).map(
          ([division, alerts], index) => {
            const Icon = divisionIcons[division] || Stethoscope;
            const hasAlerts = alerts.length > 0;
            const isExpanded = expandedDivisions[division];
            const visibleAlerts = isExpanded ? alerts : alerts.slice(0, 5);

            return (
              <div
                key={division}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden card-animate"
                style={{ animationDelay: `${(index + 5) * 50}ms` }}
                data-testid={`medical-alerts-${division.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-md bg-teal-100 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-teal-700" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">
                      {division}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {hasAlerts ? (
                    visibleAlerts.map((alert, i) => (
                      <div
                        key={`${alert.person_id}-${i}`}
                        className="px-6 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(`/medical/persons/${alert.person_id}`)
                        }
                        data-testid={`medical-alert-${alert.person_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              alert.status === "expired"
                                ? "bg-red-100 text-red-800 border border-red-300"
                                : alert.status === "missing"
                                  ? "bg-amber-100 text-amber-800 border border-amber-300"
                                  : "bg-yellow-100 text-yellow-800 border border-yellow-300"
                            }`}
                          >
                            {alert.status === "expired"
                              ? "Expired"
                              : alert.status === "missing"
                                ? "Missing"
                                : "Expiring"}
                          </span>
                          <div>
                            <span className="text-sm font-medium text-slate-900">
                              {alert.person_name}
                            </span>
                            <span className="ml-2 text-xs text-slate-400">
                              {alert.person_type}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          {alert.expiration_date ? (
                            <span>
                              {new Date(
                                alert.expiration_date
                              ).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-amber-500">No form</span>
                          )}
                          <ChevronRight className="h-4 w-4" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <div className="h-12 w-12 rounded-full bg-emerald-50 mx-auto mb-3 flex items-center justify-center">
                        <UserCheck
                          className="h-6 w-6 text-emerald-600"
                          strokeWidth={2}
                        />
                      </div>
                      <p className="text-sm text-slate-500">
                        All forms are current
                      </p>
                    </div>
                  )}
                </div>

                {alerts.length > 5 && (
                  <div className="px-6 py-3 border-t border-slate-100 text-center">
                    <button
                      onClick={() =>
                        setExpandedDivisions((prev) => ({
                          ...prev,
                          [division]: !prev[division],
                        }))
                      }
                      className="text-sm text-teal-700 hover:underline font-medium"
                    >
                      {isExpanded
                        ? "Show less"
                        : `View all ${alerts.length} alerts`}
                    </button>
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}
